import type {ReactNode} from 'react'
import {ChevronDownIcon, ChevronRightIcon, AlertIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import styles from './NewToolHeader.module.css'
import {clsx} from 'clsx'
import {useSessionLogsContext} from '../Context'
import {sendEvent} from '@github-ui/hydro-analytics'
import {WithShimmerEffect} from '@github-ui/copilot-chat/components/WithShimmerEffect'
import {StatusTextSlider} from '@github-ui/agent-sessions-interface/components/StatusTextSlider'

export interface ToolHeaderProps {
  /**
   * The leading visual element to display before the title (e.g., an icon)
   */
  leadingVisual?: ReactNode
  /**
   * The trailing visual element to display after the title (e.g., an indicator icon)
   */
  trailingVisual?: ReactNode
  /**
   * Indicates whether the file is in a collapsed state
   */
  isCollapsed?: boolean
  /**
   * Title details of tool-driven titles
   */
  title: string
  /**
   * The path to the file, if part of the args
   */
  path?: string
  /**
   * Indicates whether the tool has references rendered as children (for chat variant)
   */
  hasReferences?: boolean
  /**
   * Indicates whether the tool is in an error state
   */
  isError?: boolean
  /**
   * Indicates whether the tool is in a pending state
   */
  isPending?: boolean
  /**
   * Callback for handling modifier + click to expand/collapse all tools
   */
  onModifierClick?: (expand: boolean) => void
  /**
   * Callback for handling regular click to toggle this tool
   */
  onToggle?: () => void
  /**
   * Indicates whether the tool is part of a group
   */
  isInGroup?: boolean
  /**
   * Indicates whether this header is for a group (not a child inside a group)
   */
  isGroupHeader?: boolean
  /**
   * Muted status text shown inline after the chevron (e.g., current tool call name)
   */
  statusText?: string
}

/**
 * Props that can be passed to the Tool component to configure the ToolHeader.
 * This is a subset of ToolHeaderProps, containing only the customizable visual elements.
 */
export type ToolHeaderPropsType = Pick<ToolHeaderProps, 'leadingVisual' | 'trailingVisual'>

export function NewToolHeader({
  leadingVisual,
  trailingVisual,
  title,
  path,
  isCollapsed,
  hasReferences = true,
  isError = false,
  isPending = false,
  isInGroup = false,
  isGroupHeader = false,
  statusText,
  onModifierClick,
  onToggle,
}: ToolHeaderProps) {
  const {variant, onToolExpand} = useSessionLogsContext()

  const handleClick = (event: React.MouseEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if ((event.altKey || event.metaKey) && onModifierClick) {
      event.preventDefault()
      event.stopPropagation()
      // If currently collapsed, expand all; if expanded, collapse all
      onModifierClick(!!isCollapsed)

      sendEvent('agent_sessions.task_view', {
        target: 'TOOL_HEADER_MODIFIER',
        action: isCollapsed ? 'EXPAND_ALL' : 'COLLAPSE_ALL',
        tool_title: title,
      })

      return false // Prevent further event handling
    } else {
      // Regular click - toggle this tool
      onToggle?.()
      onToolExpand?.(!!isCollapsed)

      sendEvent('agent_sessions.task_view', {
        target: 'TOOL_HEADER',
        action: isCollapsed ? 'EXPAND' : 'COLLAPSE',
        tool_title: title,
      })
    }
  }

  if (variant === 'mission-control' || variant === 'chat') {
    return (
      <div className={styles.headerContainer}>
        <button
          type="button"
          className={clsx(styles.missionControl, {
            [styles.inGroup]: isInGroup,
            [styles.groupHeader]: isGroupHeader,
            [styles.nonInteractive]: !hasReferences,
          })}
          onClick={hasReferences ? handleClick : undefined}
          aria-disabled={!hasReferences}
          aria-expanded={!isCollapsed}
          tabIndex={hasReferences ? 0 : -1}
        >
          {leadingVisual ? (
            <span className={styles.leadingVisualContainer}>
              <span className={styles.leadingVisualIcon}>{leadingVisual}</span>
              {hasReferences && (
                <ChevronRightIcon
                  size={12}
                  className={styles.leadingVisualChevron}
                  data-expanded={!isCollapsed}
                  aria-hidden="true"
                />
              )}
            </span>
          ) : null}
          {isPending ? (
            <div className={styles.text}>
              <WithShimmerEffect className={styles.shimmerInline} innerClassName={styles.shimmerInline}>
                <span className={clsx(styles.title, {[styles.titleInGroup]: isInGroup})}>{title}</span>
              </WithShimmerEffect>
              {path && (
                <>
                  {' '}
                  <span className={styles.path}>{path}</span>
                </>
              )}
            </div>
          ) : (
            <span className={styles.text}>
              <span className={clsx(styles.title, {[styles.titleInGroup]: isInGroup})}>{title}</span>
              {path && (
                <>
                  {' '}
                  <span className={styles.path}>{path}</span>
                </>
              )}
            </span>
          )}
          {hasReferences && !leadingVisual && (
            <ChevronRightIcon size={12} className={styles.toggleIcon} data-expanded={!isCollapsed} aria-hidden="true" />
          )}
          {statusText && <StatusTextSlider text={statusText} />}
          {isError && <AlertIcon size={16} className="color-fg-attention flex-shrink-0" />}
        </button>
        {trailingVisual ? <div className={styles.trailingVisualContainer}>{trailingVisual}</div> : null}
      </div>
    )
  }

  return (
    <Button
      as="summary"
      block
      className={clsx('Button', styles.button)}
      alignContent="start"
      aria-expanded={!isCollapsed}
      aria-labelledby="tool-header-title"
      leadingVisual={isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
      trailingVisual={isError ? <AlertIcon size={16} className="color-fg-attention flex-shrink-0" /> : null}
      onClick={handleClick}
    >
      <>
        <span className="overflow-hidden text-truncate flex-1">{title}</span>{' '}
        {path && <span className="text-mono fgColor-default">{path}</span>}
      </>
    </Button>
  )
}
